import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, listMyOrganizations, setDefaultOrganization, type OrganizationWithRole } from "../api.ts";
import { useAppDispatch, useAppSelector } from '../store/hooks.ts';
import { User } from '../models/user.ts';
import type { Organization } from '../models/organization.ts';
import { setUser, UserState } from '../store/user/userSlice.ts';
import { selectCurrentOrganization, selectOrganizations, selectOrganizationsLoaded, setOrganizations, setCurrentOrganization } from '../store/organization/organizationSlice.ts';
import UserProfileCard from './UserProfileCard.tsx';
import OrganizationsList from './OrganizationsList.tsx';
import CreateOrganizationDialog from './CreateOrganizationDialog.tsx';
import {
    Box,
    Card,
    CardContent,
    Typography
} from '@mui/material';

// Utility functions
const createEmptyUserData = (): UserState => ({
    user: {
        id: '',
        name: '',
    }
});

const UserPage = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [user, setUserState] = useState<UserState>(createEmptyUserData());
    const [showCreateOrgDialog, setShowCreateOrgDialog] = useState(false);

    const currentOrganization = useAppSelector(selectCurrentOrganization);
    const organizations = useAppSelector(selectOrganizations);
    const organizationsLoaded = useAppSelector(selectOrganizationsLoaded);

    const fetchUser = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await api.get('/user');
            const userData: User = response.data;
            dispatch(setUser(userData));
            setUserState({ user: userData });

            // Immediately load organizations after user fetch to pick up pending invites
            try {
                const orgs = await listMyOrganizations();
                dispatch(setOrganizations(orgs));
            } catch (err) {
                console.error('Failed to load organizations after user fetch:', err);
            }
        } catch (error) {
            console.error('Error fetching user profile:', error);
        } finally {
            setIsLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    if (isLoading || !user.user) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>Loading...</Typography>
        </Box>;
    }

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
            {/* Profile card moved to a separate component */}
            <UserProfileCard user={user.user} onEdit={() => navigate('/profile')} />

            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Organizations
                    </Typography>

                    {/* Organizations list moved to a separate component */}
                    <OrganizationsList
                        organizations={organizations}
                        loading={!organizationsLoaded}
                        currentOrganizationId={currentOrganization?.id ?? null}
                        onSwitchOrg={async (org: OrganizationWithRole) => {
                            try {
                                if (org.id == null) {
                                    console.warn('Attempted to switch organization but org.id is undefined', org);
                                    return;
                                }
                                await setDefaultOrganization(org.id);
                                dispatch(setCurrentOrganization(org));
                                window.location.reload();
                            } catch (err) {
                                console.error('Failed to switch organization:', err);
                            }
                        }}
                        onManageOrg={(org: OrganizationWithRole) => {
                            // Ensure the org is current before navigating
                            if (currentOrganization?.id !== org.id) {
                                (async () => {
                                    try {
                                        if (org.id == null) {
                                            console.warn('Attempted to set default organization but org.id is undefined', org);
                                            return;
                                        }
                                        await setDefaultOrganization(org.id);
                                        dispatch(setCurrentOrganization(org));
                                    } catch (err) {
                                        console.error('Failed to switch organization before manage navigation:', err);
                                    } finally {
                                        navigate('/organization');
                                    }
                                })();
                            } else {
                                navigate('/organization');
                            }
                        }}
                        onCreateClick={() => setShowCreateOrgDialog(true)}
                    />

                </CardContent>
            </Card>

            {/* Create Organization Dialog moved to its own component */}
            <CreateOrganizationDialog
                open={showCreateOrgDialog}
                onClose={() => setShowCreateOrgDialog(false)}
                onCreated={async (createdOrg: Organization) => {
                    // After create, refresh organizations and switch optionally
                    const refreshedOrgs = await listMyOrganizations();
                    dispatch(setOrganizations(refreshedOrgs));
                    // Optionally switch to new org
                    if (createdOrg.id != null) {
                        try {
                            await setDefaultOrganization(createdOrg.id);
                            // Find the org with role from refreshed list
                            const orgWithRole = refreshedOrgs.find(o => o.id === createdOrg.id);
                            if (orgWithRole) {
                                dispatch(setCurrentOrganization(orgWithRole));
                            }
                            window.location.reload();
                        } catch (err) {
                            console.error('Failed to switch to newly created organization:', err);
                        }
                    }
                }}
            />
        </Box>
    );
};

export default UserPage;
